import { Company, Job, Grower, Contact, JobStatusNumber } from "../../../../models";
import { Filter, StandardAxiosConfig, getBaseDataURL } from "../../../common";
import { CreateAPI_Base, DataAPI_Base, GetAPI_Base, UpdateAPI_Base } from "../base";
import { API } from "../../..";
import type { Branch } from "../../../../models";
import axios from "axios";
import { IAirtableAttachment, JobFlags } from "../../../../utilities/types";
import { LogData } from "@rogoag/shared-ts";
import { booleanPointInPolygon } from "@turf/boolean-point-in-polygon";
import { Feature, FeatureCollection, Point } from "geojson";
import { CREATE_NEW_SALESPERSON, DEBUG } from "../../../../utilities/constants";
import { normalizePointsFC } from "../../../../domain/sample/sampleService";
import { logger } from "../../../../utilities/logger";
import { generateUUID } from "../../../../utilities/utils";

/**
 * Applies collectBoundary flag to a job by setting boundaryChangeType to "Major"
 * and adding a note indicating the customer requested boundary collection.
 * Mutates the job object in place.
 */
export function applyCollectBoundary(job: Partial<Job>): void {
	if (job.collectBoundary) {
		job.boundaryChangeType = "Major";
		const notes = job.boundaryChangeNotes?.split("\n") || [];
		notes.push("Boundary Set by Customer.");
		job.boundaryChangeNotes = notes.join("\n");
	}
}

class CreateAPI_Jobs extends CreateAPI_Base<Job> {
	constructor() {
		super("jobs");
	}

	public async many(jobs: Partial<Job>[], source?: JobFlags): Promise<Job[]> {
		const user = await API.Users.me();
		const uniqueDealIds = Array.from(new Set(jobs.filter((job) => job.dealIds?.[0]).map((job) => job.dealIds![0])));
		const deals = uniqueDealIds.length > 0 ? await API.Data.Deals.get.byIds(uniqueDealIds) : [];
		const logData: LogData = {
			function: "API.Data.Jobs.create.many",
			jobs_count: jobs.length,
			source: source,
			user: user.email,
		};

		await associateExternalGrowers();
		await addNewSalespersons();
		await associateSalespersonsAndBranches();
		const jobsToCreate = await prepareJobsForCreation();

		logData.function = "API.Data.Jobs.create.many.creatingJobs";
		logger.log("Creating jobs", logData);
		const createdJobs = await super.many(jobsToCreate);

		return createdJobs;

		async function associateSalespersonsAndBranches() {
			let contactsToUpdate: Contact[] = [];
			let branchesToUpdate: Branch[] = [];
			for (const job of jobs) {
				// Filter out any unresolved temp/sentinel IDs that shouldn't reach Airtable
				const validSalespersonIds = job.salespersonIds?.filter(
					(id) => id && !id.startsWith("temp-") && id !== CREATE_NEW_SALESPERSON,
				);
				if (validSalespersonIds && validSalespersonIds.length > 0 && job.branchIds && job.branchIds.length > 0) {
					const contacts = await API.Data.Contacts.get.byIds(validSalespersonIds);
					const branches = await API.Data.Branches.get.byIds(job.branchIds);
					for (const contact of contacts) {
						contactsToUpdate.push({
							...contact,
							salespersonBranchIds: Array.from(new Set([...(contact.salespersonBranchIds || []), ...job.branchIds])),
							isSalesperson: true,
						});
					}
					for (const branch of branches) {
						branchesToUpdate.push({
							...branch,
							salespersonIds: Array.from(new Set([...(branch.salespersonIds || []), ...validSalespersonIds])),
						});
					}
				}
			}

			contactsToUpdate = contactsToUpdate.filter(
				(contact, index, self) => contact.id && self.findIndex((c) => c.id === contact.id) === index,
			);
			branchesToUpdate = branchesToUpdate.filter(
				(branch, index, self) => branch.id && self.findIndex((b) => b.id === branch.id) === index,
			);

			if (contactsToUpdate.length == 0 && branchesToUpdate.length == 0) return;
			logData.function = "API.Data.Jobs.create.many.associateSalespersonsAndBranches";
			logger.log("Associating salespersons and branches", logData);

			await API.Data.Contacts.update.many(contactsToUpdate);
			await API.Data.Branches.update.many(branchesToUpdate);
		}

		async function addNewSalespersons() {
			const newSalespersons = jobs
				.map((job) => job.newSalesperson)
				.filter((sp) => !!sp)
				.filter((sp, idx, arr) =>
					sp?.name
						? arr.findIndex(
								(s) =>
									s?.name?.trim().toLowerCase() === sp.name.trim().toLowerCase() &&
									s?.phone?.trim() === sp.phone?.trim(),
							) === idx
						: true,
				);
			if (newSalespersons.length == 0) return;

			const newContacts: Partial<Contact>[] = [];
			for (const newSalesperson of newSalespersons) {
				if (!newSalesperson.name || newSalesperson.name.trim() === "") continue;

				const firstName = newSalesperson.name.split(" ").at(0) || "";
				const lastName = newSalesperson.name.split(" ").slice(1).join(" ") || "";

				const newContact: Partial<Contact> = {
					firstName: firstName,
					lastName: lastName,
					mobilePhone: newSalesperson.phone,
					companyIds: user.companyIds,
					isSalesperson: true,
				};
				newContacts.push(newContact);
			}

			if (newContacts.length == 0) return;

			logData.function = "API.Data.Jobs.create.many.addNewSalespersons";
			logger.log("Adding new salespersons", logData);
			const contacts = await API.Data.Contacts.create.many(newContacts);

			for (const job of jobs) {
				job.salespersonIds = job.salespersonIds?.filter(
					(id) => id !== CREATE_NEW_SALESPERSON && !id.startsWith("temp-"),
				);

				const newSalesperson = job.newSalesperson;
				if (!newSalesperson) continue;

				const salesperson = contacts.find(
					(c) => c.name.trim() === newSalesperson.name.trim() && c.mobilePhone.trim() === newSalesperson.phone.trim(),
				);
				if (!job.salespersonIds) job.salespersonIds = [];
				if (salesperson) job.salespersonIds.push(salesperson.id);
			}
		}

		async function associateExternalGrowers() {
			logData.function = "API.Data.Jobs.create.many.associateExternalGrowers";

			const externalGrowers = jobs.map((job) => job.externalGrower).filter((g) => !!g);
			if (DEBUG && externalGrowers.length > 0)
				logger.log("[DEBUG] External growers to associate", { ...logData, externalGrowers });
			let existingGrowers: Grower[] = [];
			if (externalGrowers.length > 0) {
				existingGrowers = await API.Data.Growers.get.byFilter({
					type: "OR",
					fields: externalGrowers.map((g) => ({ externalId: g.id })),
				} as Filter<Grower>);
			}
			if (DEBUG && existingGrowers.length > 0)
				logger.log("[DEBUG] Existing growers matching external IDs", { ...logData, existingGrowers });
			const newExternalGrowers = externalGrowers.filter((g) => !existingGrowers.some((eg) => eg.externalId === g.id));
			const companyId = user.companyIds.at(0);
			const companyIds = companyId ? [companyId] : [];
			const newGrowers: Partial<Grower>[] = newExternalGrowers.map((g) => ({
				name: g.name,
				companyIds: companyIds,
				externalId: g.id,
			}));
			if (DEBUG && newGrowers.length > 0)
				logger.log("[DEBUG] New external growers to create", { ...logData, newGrowers });

			logger.log("Associating external growers", logData);
			const createdGrowers = await API.Data.Growers.create.many(newGrowers);
			if (DEBUG && createdGrowers.length > 0)
				logger.log("[DEBUG] Created external growers", { ...logData, createdGrowers });
			const allGrowers = [...existingGrowers, ...createdGrowers];
			for (const job of jobs) {
				const grower = allGrowers.find((g) => g.externalId === job.externalGrower?.id);
				if (grower) job.growerIds = [grower.id];
			}
		}

		async function prepareJobsForCreation() {
			return await Promise.all(
				jobs.map(async (job) => {
					// This is a hack to ensure that we can match the job after creation,
					// in order to upload the files into the appropriate S3 bucket
					job.apiId = generateUUID();
					applyCollectBoundary(job);
					job.isPointsAttached =
						job.files?.pointsGeoJSON && job.files.pointsGeoJSON.features?.length > 0 ? true : false;

					job.jobFlags = job.jobFlags ? [...job.jobFlags] : [];
					if (source) job.jobFlags.push(source);
					if (source == "Created Portal API Agrian") job.jobFlags.push("Agrian");
					if (user.isAdmin) job.jobFlags.push("ROGO Created");

					await handleZonesForCompositeJobs(job);

					const hasZones = job.files?.zonesGeoJSON && job.files.zonesGeoJSON.features?.length > 0;
					const hasPoints = job.files?.pointsGeoJSON && job.files.pointsGeoJSON.features?.length > 0;

					if (hasZones && hasPoints) {
						logData.function = "API.Data.Jobs.create.many.prepareJobsForCreation";
						logger.log("Renumbering zone cores", { ...logData, temp_id: job.apiId });
						job.files!.pointsGeoJSON = await renumberZoneCores(job.files!.zonesGeoJSON!, job.files!.pointsGeoJSON!);
					}

					if (job.files?.boundaryGeoJSON)
						job.files.boundaryGeoJSON = cleanGeoJson(job.files.boundaryGeoJSON, "boundary");
					if (job.files?.pointsGeoJSON)
						job.files.pointsGeoJSON = normalizePointsFC(cleanGeoJson(job.files.pointsGeoJSON, "point"));
					if (job.files?.zonesGeoJSON) job.files.zonesGeoJSON = cleanGeoJson(job.files.zonesGeoJSON, "sample zone");

					return job;
				}),
			);
		}

		async function handleZonesForCompositeJobs(job: Partial<Job>) {
			if (job.dealIds && job.dealIds.length > 0) {
				if (job.files) {
					if (!job.dealIds) return;
					const deal = deals.find((d) => d.id === job.dealIds![0]);
					if (!deal) return;
					if (deal.isComposite && job.files.boundaryGeoJSON) {
						logData.function = "API.Data.Jobs.create.many.handleZonesForCompositeJobs";
						logger.log("Handling zones for composite job", { ...logData, temp_id: job.apiId });

						// In Composite jobs, the zone is the same as the boundary,
						// so we're doing the copy-and-paste here to save time in map-making
						// Deep copy to avoid mutating the original boundary
						const znsGeoJson = JSON.parse(JSON.stringify(job.files.boundaryGeoJSON));
						if (znsGeoJson.features) {
							znsGeoJson.features.forEach((feature: any) => {
								if (!feature.properties) feature.properties = {};
								feature.properties.ZoneID = 1;
							});
						}
						// Only set GeoJSON - SHP will be generated at upload time
						job.files.zonesGeoJSON = znsGeoJson;
					} else if (deal.isMultiComposite) {
						// In MultiComposite jobs, we still don't want their zones,
						// but we can't simply copy-and-paste the boundary
						job.files.zonesGeoJSON = undefined;
					}
				}
			}
		}

		/**
		 * We can't trust the core number coming from customers or external software,
		 * and they generally don't really care what the numbers are anyway,
		 * so here we ensure that the core numbers are unique, consistent, and match the zone they belong to.
		 */
		async function renumberZoneCores(
			znsGeoJson: GeoJSON.FeatureCollection,
			ptsGeoJson: GeoJSON.FeatureCollection,
		): Promise<GeoJSON.FeatureCollection> {
			// Deep copy to avoid mutating the original
			const resultGeoJson = JSON.parse(JSON.stringify(ptsGeoJson)) as GeoJSON.FeatureCollection;

			for (const zone of znsGeoJson.features) {
				let coreNumber = 0;
				for (const point of resultGeoJson.features) {
					const pointGeom = point.geometry as Point;

					if (booleanPointInPolygon(pointGeom.coordinates, zone as Feature<any>)) {
						if (zone.properties && zone.properties.ZoneID) {
							coreNumber++;
							point.properties = point.properties || {};
							point.properties.CoreID = `${zone.properties.ZoneID}.${coreNumber}`;
						}
					}
				}
			}

			return resultGeoJson;
		}
	}

	async one(
		job: Partial<Job>,
		source?: "Created Portal Single" | "Created Portal Bulk" | "Created Portal API Proagrica",
	): Promise<Job> {
		return (await this.many([job], source))[0];
	}
}

class GetAPI_Jobs extends GetAPI_Base<Job> {
	constructor() {
		super("jobs", Job, true);
	}

	public async byCompany(company: Company) {
		if (!company.name) return [];
		const filter: Filter<Job> = { fields: { companyName: company.name } };
		const jobs = await super.byFilter(filter);
		return jobs;
	}

	/**
	 * Retrieves jobs associated with the specified growers.
	 *
	 * @param growers - An array of `Grower` objects to filter jobs by. If empty or undefined, returns an empty array.
	 * @param isUnreadyOnly - Optional. If `true`, filters jobs to only those that are unready (`statusNumber` is `0`). Defaults to `false`.
	 */
	public async byGrowers(growers: Grower[], isUnreadyOnly: boolean = false): Promise<Job[]> {
		if (!growers || growers.length === 0) return [];
		const growerFilter: Filter<Job> = {
			type: "OR",
			fields: [
				// We have to search both, because Grower names are not normalized
				...growers.map((g) => ({ growerIds: [g.name] })),
				...growers.map((g) => ({ growerName: g.name })),
			],
		};
		const unreadyFilter: Filter<Job> = {
			type: "AND",
			fields: [{ statusNumber: JobStatusNumber.Unready }, growerFilter],
		};
		const jobs = await super.byFilter(isUnreadyOnly ? unreadyFilter : growerFilter);
		return jobs;
	}

	public async lastCropOptions(): Promise<string[]> {
		const url = `${getBaseDataURL("jobs")}/options/last-crop`;
		const response = await axios.get<string[]>(url, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to get last crop options`);
		} else {
			return response.data;
		}
	}

	public async nextCropOptions(): Promise<string[]> {
		const url = `${getBaseDataURL("jobs")}/options/next-crop`;
		const response = await axios.get<string[]>(url, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to get next crop options`);
		} else {
			return response.data;
		}
	}
}

class UpdateAPI_Jobs extends UpdateAPI_Base<Job> {
	constructor() {
		super("jobs");
	}

	async files(id: string, fileList: File[]) {
		const url = `${getBaseDataURL("jobs")}/upload-file/${id}`;

		async function upload(file: File) {
			const formData = new FormData();
			formData.append("file", file);

			const response = await axios.post(url, formData, StandardAxiosConfig(10, { isFileUpload: true }));
			if (response.status !== 200) {
				throw new Error(`Failed to upload file: ${file.name}`);
			} else {
				return response.data;
			}
		}

		if (!fileList) return [];
		const fileArray = Array.from(fileList);
		const uploadResults = await Promise.all(
			fileArray.map(async (file) => {
				const s3url = await upload(file);
				return { url: s3url, filename: file.name } as IAirtableAttachment;
			}),
		);

		return uploadResults;
	}
}

class JobIssuesAPI extends DataAPI_Base<Job> {
	constructor() {
		super("jobs", Job);
	}

	async getAgrian(): Promise<Job[]> {
		const url = `${getBaseDataURL("jobs")}/issues/agrian`;
		const response = await axios.get<Job[]>(url, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to get Agrian issues`);
		} else {
			return response.data.map((j) => Object.assign(new Job(), j));
		}
	}

	async fixAgrian(ids: string[]): Promise<void> {
		const url = `${getBaseDataURL("jobs")}/issues/agrian`;
		const response = await axios.post(url, ids, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to fix Agrian issues`);
		} else {
			return response.data;
		}
	}

	async getUnmatchedDealJobs(): Promise<Job[]> {
		const url = `${getBaseDataURL("jobs")}/issues/deal-matching`;
		const response = await axios.get<Job[]>(url, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to get unmatched deal jobs`);
		} else {
			return response.data.map((j) => Object.assign(new Job(), j));
		}
	}

	async fixUnmatchedDealJobs(ids: string[]): Promise<void> {
		const url = `${getBaseDataURL("jobs")}/issues/deal-matching`;
		const response = await axios.post(url, ids, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to fix unmatched deal jobs`);
		} else {
			return response.data;
		}
	}

	async getPostSamplingStuckJobs(): Promise<Job[]> {
		const url = `${getBaseDataURL("jobs")}/issues/post-sampling`;
		const response = await axios.get<Job[]>(url, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to get post-sampling stuck jobs`);
		} else {
			return response.data.map((j) => Object.assign(new Job(), j));
		}
	}

	async fixPostSamplingStuckJobs(ids: string[]): Promise<void> {
		const url = `${getBaseDataURL("jobs")}/issues/post-sampling`;
		const response = await axios.post(url, ids, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to fix post-sampling stuck jobs`);
		} else {
			return response.data;
		}
	}
}
export class JobsAPI extends DataAPI_Base<Job> {
	get: GetAPI_Jobs;
	create: CreateAPI_Jobs;
	update: UpdateAPI_Jobs;
	issues: JobIssuesAPI;

	// job data is compressed, as it can get quite large
	constructor() {
		super("jobs", Job, true);
		this.get = new GetAPI_Jobs();
		this.create = new CreateAPI_Jobs();
		this.update = new UpdateAPI_Jobs();
		this.issues = new JobIssuesAPI();
	}
}

function cleanGeoJson(geojson: FeatureCollection, type?: string): FeatureCollection {
	const cleanedGeoJson = JSON.parse(JSON.stringify(geojson)) as FeatureCollection;
	for (const feature of cleanedGeoJson.features) {
		if (feature.properties) {
			if ("__uniqueJobKey" in feature.properties) delete feature.properties.__uniqueJobKey;
			if ("FilePath" in feature.properties) delete feature.properties.FilePath;
			for (let i = 1; i <= 9; i++) {
				if (`FolderLevel${i}` in feature.properties) delete feature.properties[`FolderLevel${i}`];
			}
		}
		if (type) {
			if (!feature.properties) feature.properties = {};
			feature.properties.type = type;
		}
	}
	return cleanedGeoJson;
}
